import {yupResolver} from "@hookform/resolvers/yup";
import {FormProvider, useForm} from "react-hook-form";
import {useSearchParams} from "react-router";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {CheckBoxField} from "@/components/form/fields/CheckBoxField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {TextAreaField} from "@/components/form/fields/TextAreaField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {RequiredFieldsNote} from "@/components/form/requiredFieldsNote/RequiredFieldsNote.tsx";
import {URL_PRIVACY_POLICY, URL_TERMS_AND_CONDITIONS} from "@/lib/links.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {ContactFormData, ContactFormDataSchema} from "@/pages/website/contact/contactFormTypes.ts";

type ContactFormProps = {
  onSubmit: (data: ContactFormData) => Promise<void>;
  serverErrors?: ServerErrors;
};

export const ContactForm = ({onSubmit, serverErrors}: ContactFormProps) => {
  const {user} = useCurrentUserContext();
  const [searchParams] = useSearchParams();
  const defaultValues: ContactFormData = {
    agree: false,
    realAgree: false,
    startTime: new Date(),
    name: user ? `${user?.first_name} ${user?.last_name}` : "",
    email: user?.email || "",
    subject: searchParams.get("subject") || "",
    message: searchParams.get("message") || "",
  };

  const methods = useForm<ContactFormData>({
    resolver: yupResolver<ContactFormData>(ContactFormDataSchema),
    defaultValues: defaultValues,
  });

  return (
    <FormProvider {...methods}>
      <form id="contact-form" onSubmit={methods.handleSubmit(onSubmit, console.log)} noValidate>
        {serverErrors?.__all__ && <FormErrorAlerts errors={serverErrors?.__all__} />}

        <RequiredFieldsNote />

        <div className="row mb-1">
          <div className="col mb-3">
            <InputField name="name" label="Name" required={true} />
          </div>
        </div>
        <div className="row mb-1">
          <div className="col mb-3">
            <InputField name="email" label="Email" required={true} />
          </div>
        </div>
        <div className="row mb-1">
          <div className="col mb-3">
            <InputField name="subject" label="Subject" required={true} />
          </div>
        </div>
        <div className="row mb-1">
          <div className="col mb-3">
            <TextAreaField name="message" label="Message" required={true} rows={8} />
          </div>
        </div>
        <div className="d-none">
          <div>
            <CheckBoxField label="I Agree to the Terms and Conditions and Privacy Policy" name="agree" helpText="" />
          </div>
        </div>
        <div className="row mb-1">
          <div className="col mb-3">
            <CheckBoxField
              required={true}
              label={
                <>
                  I agree to the{" "}
                  <a target="_blank" href={URL_TERMS_AND_CONDITIONS}>
                    Terms &amp; Conditions
                  </a>{" "}
                  and{" "}
                  <a target="_blank" href={URL_PRIVACY_POLICY}>
                    Privacy Policy
                  </a>
                </>
              }
              name="realAgree"
            />
          </div>
        </div>
        <div className="row mb-1">
          <div className="col mb-3">
            <Button type="submit">Submit</Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
